import el from "@cypherpotato/el";
import { displayFormControl } from "../ui/alert";

const TOTAL_DIGITS = 6;

export function TotpCodeInput(options = {}) {
    const input = el("div.totp-code-input[form-slot]", {
        $functions: {
            focus() {
                this.querySelector("input")?.focus();
            },
            getValue() {
                return Array.from(this.querySelectorAll("input"), item => item.value).join("");
            },
            setValue(value) {
                const digits = String(value || "").replace(/\D/g, "").slice(0, TOTAL_DIGITS);
                const inputs = this.querySelectorAll("input");
                inputs.forEach((item, index) => {
                    item.value = digits[index] || "";
                });
                const next = inputs[Math.min(digits.length, TOTAL_DIGITS - 1)];
                next?.focus();
            }
        },
        $properties: {
            value: {
                get() {
                    return this.getValue();
                },
                set(value) {
                    this.setValue(value);
                }
            }
        },
        init() {
            for (let index = 0; index < TOTAL_DIGITS; index++) {
                this.appendChild(el("input.totp-code-digit", {
                    type: "text",
                    inputmode: "numeric",
                    maxlength: index === 0 ? TOTAL_DIGITS : 1,
                    autocomplete: index === 0 ? "one-time-code" : "off",
                    "aria-label": `Authentication code digit ${index + 1}`,
                    "data-index": index,
                    onInput() {
                        if (index === 0 && this.value.length > 1) {
                            input.setValue(this.value);
                            return;
                        }

                        this.value = this.value.replace(/\D/g, "").slice(-1);
                        if (this.value && index < TOTAL_DIGITS - 1) {
                            input.querySelectorAll("input")[index + 1].focus();
                        }
                    },
                    onKeyDown(event) {
                        const inputs = input.querySelectorAll("input");
                        if (event.key === "Backspace" && this.value === "" && index > 0) {
                            inputs[index - 1].focus();
                            inputs[index - 1].value = "";
                        }
                        if (event.key === "ArrowLeft" && index > 0) {
                            event.preventDefault();
                            inputs[index - 1].focus();
                        }
                        if (event.key === "ArrowRight" && index < TOTAL_DIGITS - 1) {
                            event.preventDefault();
                            inputs[index + 1].focus();
                        }
                    },
                    onPaste(event) {
                        event.preventDefault();
                        input.setValue((event.clipboardData || window.clipboardData).getData("text"));
                    },
                    onFocus() {
                        this.select();
                    }
                }));
            }

            if (options.autofocus) {
                this.querySelector("input")?.focus();
            }
        }
    });

    return input;
}

export function promptTotpCode(message = "Enter the 6-digit code from your authenticator app.") {
    const waitDialog = window.currentWaitDialog;
    if (waitDialog) {
        waitDialog.hidden = true;
    }

    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = callback => {
            if (settled) return;
            settled = true;
            if (waitDialog) {
                waitDialog.hidden = false;
            }
            callback();
        };

        displayFormControl({
            title: "Two-factor authentication",
            message,
            formControlItem: TotpCodeInput({ autofocus: true }),
            dialogSize: "small",
            validate: [
                [value => /^\d{6}$/.test(String(value || "")), "Enter the 6-digit authentication code."]
            ],
            onConfirm(value) {
                finish(() => resolve(String(value)));
            },
            onCancel() {
                finish(() => reject(new Error("Two-factor authentication was cancelled.")));
            }
        });
    });
}
